import { MessageType } from "../enums/message.enum";
import { IMessageModel } from "../models/message.model";
import { IApiErrorModel } from "../models/api-error.model";
import { AppConfigUtil } from "./app-config.util";

export class AppUtil {
	private static overrideLogFlag: boolean = null; //assigning default value to overrideLogFlag as defined in app.json 'debug' property.

	public static getAppType() {
		return 1;
	}
	/**
	 * This function return consoleLogflag value, to determine whether console logging is ON or OFF.
	 *
	 * @returns consoleLogFlag
	 */
	public static getConsoleLogFlag(): boolean {
		let consoleLogFlag = false;
		if (this.getOverrideLogFlag() === null && this.getAppConfigLogFlag()) {
			consoleLogFlag = true;
		} else if (this.getOverrideLogFlag()) {
			consoleLogFlag = true;
		}
		return consoleLogFlag;
	}

	/**
	 * This function will toggle overrideLogFlag
	 */
	public static toggleOverrideLogFlag(): void {
		if (this.getOverrideLogFlag()) {
			this.setOverrideLogFlag(false);
		} else {
			this.setOverrideLogFlag(true);
		}
	}

	/**
	 * Function getApiErrors to scan error object of micro-services response
	 * Return IMessageModel
	 */
	public static getApiErrors(
		errors: Array<IApiErrorModel>
	): Array<IMessageModel> {
		let apiErrors: Array<IMessageModel>;
		let apiError: IMessageModel;
		if (errors && errors.length > 0) {
			apiErrors = [];
			apiError = {
				type: MessageType.ERROR,
				messages: []
			};
			errors.forEach((error: IApiErrorModel) => {
				apiError.messages.push(error.message);
			});
			apiErrors.push(apiError);
		}
		this.log("Api Error ==> %o", apiError);
		return apiErrors;
	}

	public static isEmptyObject(obj) {
		return obj && Object.keys(obj).length === 0;
	}

	/**
	 * Function isNotNull will return true if object is not null or undefined
	 */
	public static isNotNull(inObject: Object): boolean {
		let isNotNull = false;
		if (!this.isNull(inObject)) {
			isNotNull = true;
		}
		return isNotNull;
	}

	/**
	 * Function isNull will return true if object is null or undefined
	 */
	public static isNull(inObject: Object): boolean {
		let isNull = false;
		if (typeof inObject === "undefined" || inObject === null) {
			isNull = true;
		}
		return isNull;
	}

	/**
	 * Function isNotEmpty will return true if array of object is not null, undefined or empty
	 */
	public static isNotEmpty(objects: Array<Object>): boolean {
		let isNotEmpty = false;
		if (!this.isEmpty(objects)) {
			isNotEmpty = true;
		}
		return isNotEmpty;
	}

	/**
	 * Function isEmpty will return true if array of object is null, undefined or empty
	 */
	public static isEmpty(objects: Array<Object>): boolean {
		let isEmpty = false;
		if (
			typeof objects === "undefined" ||
			objects === null ||
			objects.length <= 0
		) {
			isEmpty = true;
		}
		return isEmpty;
	}

	/**
	 * This funciton removes duplicate entries from an array.
	 * @param arr e.g.
	 * [{source:'XRE1',correlationId:"6dcd6bb9-0f6d-495b-84c0-986430fed197",csrDescription:"Customer has an issue with their TV services"},
	 * {source:'XRE2',correlationId:"6dcd6bb9-0f6d-495b-84c0-986430fed198",csrDescription:"Customer has an issue with their TV services"},
	 * {source:'XRE3',correlationId:"6dcd6bb9-0f6d-495b-84c0-986430fed197",csrDescription:"Customer has an issue with their TV services"}]
	 * @param prop e.g. 'correlationId'
	 * @return  e.g.
	 * [{source:'XRE2',correlationId:"6dcd6bb9-0f6d-495b-84c0-986430fed198",csrDescription:"Customer has an issue with their TV services"},
	 * {source:'XRE3',correlationId:"6dcd6bb9-0f6d-495b-84c0-986430fed197",csrDescription:"Customer has an issue with their TV services"}]
	 */
	public static removeDuplicateEntriesFromArray(arr, prop): Array<Object> {
		const newArr: Array<Object> = new Array<Object>();
		const lookup: Object = new Object();
		for (const i in arr) {
			if (i) {
				lookup[arr[i][prop]] = arr[i];
			}
		}
		for (const i in lookup) {
			if (i) {
				newArr.push(lookup[i]);
			}
		}
		return newArr;
	}

	/**
	 * Function log is a replacement for console.log
	 */
	public static log(...args): void {
		const appProperties: any = AppConfigUtil.getProperties();

		if (appProperties && appProperties.debug) {
			// tslint:disable-next-line
			console.log.apply(console, args);
		}
	}

	/**
	 * Function info is a replacement for console.info
	 */
	public static info(...args): void {
		// tslint:disable-next-line
		console.info.apply(console, args);
	}

	/**
	 * Function warn is a replacement for console.warn
	 */
	public static warn(...args): void {
		// tslint:disable-next-line
		console.warn.apply(console, args);
	}

	/**
	 * Function error is a replacement for console.error
	 */
	public static error(...args): void {
		// tslint:disable-next-line
		console.error.apply(console, args);
	}
	/**
	 * This function is used to convert temprature from Farenhit to Celcius
	 * @param tempInFarenheit e.g. 100
	 * @return return temprature in Celcius as a whole number e.g 38
	 */
	public static getTempInCelcius(tempInFarenheit: number): number {
		let tempInCelcius: number;
		tempInCelcius = Math.round((tempInFarenheit - 32) * (5 / 9));
		return tempInCelcius;
	}

	/**
	 * This function is used to merge source objects.
	 */
	public static mergeSourceObjects(...sources: Array<Object>): Object {
		let mergedSources: Object;
		if (sources && sources.length > 0) {
			for (const source of sources) {
				if (source) {
					mergedSources = Object.assign({}, mergedSources, source);
				}
			}
		}
		return mergedSources;
	}

	/**
	 * This function is used to get appConfigLogFlag based on property 'debug' defined in app.json(AppConfigUtil.getProperties().debug).
	 */
	private static getAppConfigLogFlag(): boolean {
		const appProperties = AppConfigUtil.getProperties();
		return appProperties && appProperties.debug;
	}

	/**
	 * This function is used to get overrideLogFlag.
	 */
	private static getOverrideLogFlag(): boolean {
		return AppUtil.overrideLogFlag;
	}

	/**
	 * This function is used to set overrideLogFlag.
	 */
	private static setOverrideLogFlag(inOverrideLogFlag: boolean): void {
		AppUtil.overrideLogFlag = inOverrideLogFlag;
	}
}
